<template>
  <div class="col-12 col-xl-12">
    <div
      class="card mb-3 mb-xxl-5 me-xxl-4"
      style="margin-top: 1rem; margin-top: 4rem; overflow: visible"
    >
      <!-- Card Header -->
      <div
        class="card-header p-0 position-relative mt-n4 mx-3 z-index-2"
        style="height: 50px; height: 60px"
      >
        <div
          class="bg-gradient-primary shadow-primary border-radius-lg pt-3 pb-2 pt-4 pb-3 h-100"
        >
          <h6
            class="text-white text-capitalize ps-3 pe-3"
            style="font-size: 0.9rem; font-size: 1rem"
          >
            {{ t('guardians.table.title') }}
          </h6>
        </div>
      </div>

      <!-- Card Body -->
      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <!-- Loading Spinner -->
          <LoadingSpinner
            v-if="loading && guardians.length === 0"
            :message="t('guardians.table.loading')"
            class="py-3 py-5"
          />

          <template v-else>
            <!-- Mobile View -->
            <div class="d-block d-xl-none">
              <div
                v-if="!loading && guardians.length === 0"
                class="text-center py-3 text-muted"
              >
                <i class="fas fa-users-slash fa-2x mb-2"></i>
                <p>{{ t('guardians.table.noData') }}</p>
              </div>

              <div
                v-for="guardian in guardians"
                :key="'mobile-' + guardian.id"
                class="card mb-3 mx-2"
              >
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ guardian.username }}</h6>
                      <p class="text-xs text-muted mb-0">
                        {{ guardian.email || t('guardians.table.noData') }}
                      </p>
                    </div>
                    <div>
                      <span
                        class="badge badge-sm"
                        :class="guardian.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ guardian.gender === 'M' ? t('guardians.table.badges.male') : t('guardians.table.badges.female') }}
                      </span>
                    </div>
                    <div class="me-2">
                      <span
                        class="badge badge-sm"
                        :class="guardian.is_active ? 'bg-success' : 'bg-danger'"
                      >
                        {{ guardian.is_active ? t('guardians.table.badges.active') : t('guardians.table.badges.inactive') }}
                      </span>
                    </div>
                  </div>

                  <div class="d-flex justify-content-between align-items-center mt-2">
                    <span class="text-xs">
                      <i class="fas fa-phone me-1"></i> {{ guardian.phone_num }}
                    </span>

                    <div class="d-inline-flex">
                      <router-link
                        v-if="guardian.id && canSeeWallet"
                            v-can="'guardian_wallet.display'"

                        :to="{ name: 'guardians.wallet', params: { id: guardian.id } }"
                        class="btn btn-link text-warning p-0 mx-1"
                        :title="t('guardians.table.actions.wallet')"
                      >
                        <i class="fas fa-wallet fa-xs"></i>
                      </router-link>

                      <router-link
                        v-if="guardian.id"

                        :to="{ name: 'guardians.students', params: { id: guardian.id } }"
                        class="btn btn-link text-info p-0 mx-1"
                        :title="t('guardians.table.actions.students')"
                        v-can="'student_guardians.display'"
                      >
                        <i class="fas fa-users fa-xs"></i>
                      </router-link>

                      <router-link
                        v-if="guardian.id"
                        :to="{ name: 'guardians.edit', params: { id: guardian.id } }"
                        class="btn btn-link text-secondary p-0 mx-1"
                        :title="t('guardians.table.actions.edit')"
                            v-can="'guardians.update'"
                      >
                        <i class="fas fa-edit fa-xs"></i>
                      </router-link>

                      <button
                        @click="openDeleteDialog(guardian)"
                        class="btn btn-link text-danger p-0 mx-1"
                        :title="t('guardians.table.actions.delete')"
                        :disabled="isProcessing"
                            v-can="'guardians.delete'"
                      >
                        <i class="fas fa-trash fa-xs"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Desktop View -->
            <table class="table align-items-center mb-0 d-none d-xl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ t('guardians.table.columns.name') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">
                    {{ t('guardians.table.columns.email') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ t('guardians.table.columns.gender') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ t('guardians.table.columns.status') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">
                    {{ t('guardians.table.columns.phone') }}
                  </th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">
                    {{ t('guardians.table.columns.actions') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && guardians.length === 0">
                  <td colspan="6" class="text-center py-4 text-muted">
                    <i class="fas fa-users-slash fa-2x mb-2"></i>
                    <p>{{ t('guardians.table.noData') }}</p>
                  </td>
                </tr>

                <template v-if="!loading">
                  <tr v-for="guardian in guardians" :key="guardian.id">
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ guardian.username }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">
                        {{ guardian.email || t('guardians.table.noData') }}
                      </p>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="guardian.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ guardian.gender === 'M' ? t('guardians.table.badges.male') : t('guardians.table.badges.female') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="guardian.is_active ? 'bg-success' : 'bg-danger'"
                      >
                        {{ guardian.is_active ? t('guardians.table.badges.active') : t('guardians.table.badges.inactive') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">{{ guardian.phone_num }}</span>
                    </td>
                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <router-link
                          v-if="guardian.id && canSeeWallet"
                          :to="{ name: 'guardians.wallet', params: { id: guardian.id } }"
                          class="btn btn-link text-warning p-1 mx-1"
                          :title="t('guardians.table.actions.wallet')"
                            v-can="'guardian_wallet.display'"
                        >
                          <i class="fas fa-wallet fa-sm"></i>
                        </router-link>

                        <router-link
                          v-if="guardian.id"
                          :to="{ name: 'guardians.students', params: { id: guardian.id } }"
                          class="btn btn-link text-info p-1 mx-1"
                          :title="t('guardians.table.actions.students')"
                            v-can="'student_guardians.display'"
                        >
                          <i class="fas fa-users fa-sm"></i>
                        </router-link>

                        <router-link
                          v-if="guardian.id"
                          :to="{ name: 'guardians.edit', params: { id: guardian.id } }"
                          class="btn btn-link text-secondary p-1 mx-1"
                          :title="t('guardians.table.actions.edit')"
                            v-can="'guardians.update'"
                        >
                          <i class="fas fa-edit fa-sm"></i>
                        </router-link>

                        <button
                          @click="openDeleteDialog(guardian)"
                          class="btn btn-link text-danger p-1 mx-1"
                          :title="t('guardians.table.actions.delete')"
                          :disabled="isProcessing"
                            v-can="'guardians.delete'"
                        >
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, defineProps, defineEmits, computed } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { useStore } from "vuex";
import Cookies from "js-cookie";

const { t } = useI18n();
const store = useStore();


const userRoleSlug = computed(() => {
  // 1) Vuex first
  const vuexRole =
    store.state?.user?.role?.slug ||
    store.state?.user?.role ||           // if it's already a string
    store.state?.auth?.user?.role?.slug || // fallback in case some pages still use auth module
    store.state?.auth?.user?.role;

  if (typeof vuexRole === "string" && vuexRole) return vuexRole;

  // 2) localStorage fallback
  try {
    const lsUser = JSON.parse(localStorage.getItem("user") || "null");
    const lsRole = lsUser?.role?.slug || lsUser?.role;
    if (typeof lsRole === "string" && lsRole) return lsRole;
  } catch (_) {// ignore JSON parse errors
  }

  // 3) cookie fallback
  const cookieRole = Cookies.get("user_type");
  if (cookieRole) return cookieRole;

  return ""; // unknown
});

// who can see wallet?
const canSeeWallet = computed(() =>
  ["school_admin", "finance"].includes(userRoleSlug.value)
);

defineProps({
  guardians: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const isProcessing = ref(false);
const emit = defineEmits(["delete-guardian"]);

const openDeleteDialog = (guardian) => {
  emit("delete-guardian", guardian);
};
</script>
